import React, { useState } from 'react';
import { Shield, RefreshCw, Info, X } from 'lucide-react';

export function RedoCoverageToggle({ coverage, enabled, onToggle, compact = false }) {
  const [showInfo, setShowInfo] = useState(false);

  if (!coverage.eligible || coverage.loading) return null;

  const price = coverage.price?.toFixed(2);

  if (compact) {
    return (
      <div className="border border-zinc-800 bg-zinc-900/80 p-3" data-testid="redo-coverage-compact">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-zinc-300">Free Returns Coverage</span>
            <button
              onClick={() => setShowInfo(true)}
              className="text-zinc-500 hover:text-zinc-300 transition-colors"
              data-testid="redo-info-btn"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-400">+${price}</span>
            <button
              onClick={() => onToggle(!enabled)}
              className={`relative w-10 h-5 rounded-full transition-colors duration-300 ${
                enabled ? 'bg-emerald-500' : 'bg-zinc-700'
              }`}
              data-testid="redo-toggle"
            >
              <span
                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform duration-300 ${
                  enabled ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>
        {showInfo && <RedoInfoModal onClose={() => setShowInfo(false)} />}
      </div>
    );
  }

  return (
    <div data-testid="redo-coverage-toggle">
      <div className="border border-zinc-800 bg-zinc-900/60 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-zinc-800/50 bg-zinc-900/80">
          <Shield className="w-4.5 h-4.5 text-emerald-400" />
          <span className="text-sm text-white font-medium tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
            Free Returns Protection
          </span>
          <button
            onClick={() => setShowInfo(true)}
            className="ml-auto text-zinc-500 hover:text-zinc-300 transition-colors"
            data-testid="redo-info-btn"
          >
            <Info className="w-4 h-4" />
          </button>
        </div>

        {/* Options */}
        <div className="grid grid-cols-2 gap-0">
          <button
            onClick={() => onToggle(true)}
            className={`flex flex-col items-center justify-center p-4 transition-all duration-300 border-r border-zinc-800/50 ${
              enabled
                ? 'bg-emerald-500/10 border-b-2 border-b-emerald-400'
                : 'bg-transparent hover:bg-zinc-800/30 border-b-2 border-b-transparent'
            }`}
            data-testid="redo-enable-btn"
          >
            <div className="flex items-center gap-2 mb-1">
              <RefreshCw className={`w-4 h-4 ${enabled ? 'text-emerald-400' : 'text-zinc-500'}`} />
              <span className={`text-sm font-semibold ${enabled ? 'text-emerald-400' : 'text-zinc-400'}`}>
                Yes, protect my order
              </span>
            </div>
            <span className={`text-xs ${enabled ? 'text-emerald-400/80' : 'text-zinc-600'}`}>
              +${price} for free returns
            </span>
          </button>

          <button
            onClick={() => onToggle(false)}
            className={`flex flex-col items-center justify-center p-4 transition-all duration-300 ${
              !enabled
                ? 'bg-zinc-800/40 border-b-2 border-b-zinc-500'
                : 'bg-transparent hover:bg-zinc-800/30 border-b-2 border-b-transparent'
            }`}
            data-testid="redo-disable-btn"
          >
            <span className={`text-sm font-semibold ${!enabled ? 'text-zinc-300' : 'text-zinc-500'}`}>
              No thanks
            </span>
            <span className={`text-xs ${!enabled ? 'text-zinc-500' : 'text-zinc-700'}`}>
              Standard return policy
            </span>
          </button>
        </div>
      </div>

      {showInfo && <RedoInfoModal onClose={() => setShowInfo(false)} />}
    </div>
  );
}

function RedoInfoModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-zinc-900 border border-zinc-700 max-w-md w-full p-6 relative"
        onClick={e => e.stopPropagation()}
        data-testid="redo-info-modal"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors">
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-white text-lg font-semibold" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Free Returns by Redo
            </h3>
            <p className="text-zinc-500 text-xs">Hassle-free returns coverage</p>
          </div>
        </div>

        <div className="space-y-4 text-sm text-zinc-300">
          <div className="flex items-start gap-3">
            <RefreshCw className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-white mb-0.5">Free return shipping</p>
              <p className="text-zinc-500 text-xs">Prepaid return labels — no shipping cost to you</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-white mb-0.5">Package protection</p>
              <p className="text-zinc-500 text-xs">Coverage against lost, stolen, or damaged packages</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-white mb-0.5">Easy process</p>
              <p className="text-zinc-500 text-xs">Start a return online in minutes through our returns portal</p>
            </div>
          </div>
        </div>

        <p className="text-zinc-600 text-[10px] mt-5 pt-4 border-t border-zinc-800">
          Powered by Redo — coverage is applied at checkout and is non-refundable.
        </p>
      </div>
    </div>
  );
}

export function RedoPDPBadge({ coverage }) {
  if (!coverage.eligible || coverage.loading) return null;

  return (
    <div className="flex items-center gap-2 text-xs text-zinc-400" data-testid="redo-pdp-badge">
      <Shield className="w-3.5 h-3.5 text-emerald-400" />
      <span>
        Free returns coverage available at checkout — <span className="text-emerald-400">powered by Redo</span>
      </span>
    </div>
  );
}
